import React, {useContext, useEffect, useState} from "react";
import PropTypes from "prop-types";
import {FormattedMessage} from "react-intl";

import {Box, CircularProgress} from "@mui/material";

import SectionHeader from "components/dashboard/SectionHeader";
import ToggleNavigation from "components/Toggle/ToggleNavigation";
import Toggle from "components/Toggle/Toggle";

import StockAlertKpis from "./components/StockAlertKpis";
import StockAlertsTable from "./components/StockAlertsTable";
import CategoriesBarChart from "./components/CategoriesBarChart";
import StockAlertMobileList from "./components/StockAlertMobileList";
import ShowMoreButton from "../TopTablesComponents/ShowMoreButton";
import TopTableHeaderContainer from "../TopTablesComponents/TopTableHeaderContainer";
import TopTableContainer from "../TopTablesComponents/TopTableContainer";
import ExportButton from "../TopTablesComponents/ExportButton";
import TopTableHeaderActionContainer from "../TopTablesComponents/TopTableHeaderActionContainer";
import ShowMoreButtonMobile from "../TopTablesComponents/ShowMoreButtonMobile";
import TopRetailerStockAlert from "./components/TopRetailerStockAlert";

import {DateRangePickerContext} from "contexts/DateRangePickerContext";

import {getStockAlerts} from "api/dashboard/stockAlerts";
import {exportStockAlertsCsv} from "api/dashboard/csvExport";

import useScrollTrigger from "hooks/useScrollTrigger";

import {UserAgentUtils} from "utils/useragent";

const isMobile = UserAgentUtils.isMobile();

function StockAlerts({retailer, currency, entityName}) {
    const {startDate, endDate, dateChangedTrigger} = useContext(DateRangePickerContext);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [sortMode, setSortMode] = useState(0);

    const [sectionRef, triggered] = useScrollTrigger(dateChangedTrigger);

    useEffect(() => {
        if (!triggered || !startDate || !endDate) {
            return;
        }

        const start = startDate.format("Y-M-D");
        const end = endDate.format("Y-M-D");

        // a slower response for a previous date range must not overwrite the current one
        let cancelled = false;

        setData(null);
        setLoading(true);

        getStockAlerts(start, end, retailer)
            .then(res => !cancelled && setData(res?.stockAlerts ?? null))
            .catch(() => !cancelled && setData(null))
            .finally(() => !cancelled && setLoading(false));

        return () => {
            cancelled = true;
        };
    }, [triggered, retailer]);

    const categories = data?.byCategory ?? [];
    const allProducts = data?.products ?? [];
    const products = showAll ? allProducts : allProducts.slice(0, 5);
    const displayData = data ? {...data, products} : null;

    const tooFewAlerts = !loading && data !== null && (data.totalAlerts ?? 0) < 5;

    return (
        <div ref={sectionRef}>
            {loading && (
                <Box sx={{display: "flex", justifyContent: "center", py: 4, mt: 4}}>
                    <CircularProgress size={24}/>
                </Box>
            )}
            {!loading && !tooFewAlerts && (
                <>
                    <div>
                        <SectionHeader
                            title={<FormattedMessage id="dashboard.stockAlerts.section.title" />}
                            subtitle={<FormattedMessage id="dashboard.stockAlerts.section.subtitle" />}
                        />
                        <StockAlertKpis data={data} loading={loading} currency={currency}/>
                        <CategoriesBarChart
                            categories={categories}
                            currency={currency}
                            loading={loading}
                            totalAlerts={data?.totalAlerts ?? 0}
                        />
                        {!retailer && (
                            <TopRetailerStockAlert currency={currency} />
                        )}
                    </div>
                    <TopTableHeaderContainer>
                        <SectionHeader
                            title={<FormattedMessage id="dashboard.stockAlerts.title"/>}
                            subtitle={<FormattedMessage id="dashboard.stockAlerts.subtitle"/>}
                        />
                        <TopTableHeaderActionContainer>
                            <ToggleNavigation
                                value={sortMode}
                                onChange={(value) => setSortMode(value)}
                                autoMargin
                                fullWidth={isMobile}
                            >
                                <Toggle
                                    fullWidth={isMobile}
                                    label={<FormattedMessage id='dashboard.stockAlerts.toggle.value' />}
                                    autoHeight
                                />
                                <Toggle
                                    fullWidth={isMobile}
                                    label={<FormattedMessage id='dashboard.stockAlerts.toggle.volume' />}
                                    autoHeight
                                />
                            </ToggleNavigation>
                            <ExportButton
                                disabled={false}
                                onClick={() => {
                                    exportStockAlertsCsv(
                                        data?.exportRows ?? [],
                                        startDate.format("Y-M-D"),
                                        endDate.format("Y-M-D"),
                                        entityName
                                    )
                                }}
                            />
                        </TopTableHeaderActionContainer>
                    </TopTableHeaderContainer>
                    <TopTableContainer>
                        {UserAgentUtils.isMobile() ? (
                            <>
                                <StockAlertMobileList
                                    data={displayData}
                                    loading={loading}
                                    currency={currency}
                                    sortMode={sortMode}
                                />
                                {!showAll && allProducts.length > 5 && (
                                    <ShowMoreButtonMobile onClick={() => setShowAll(true)} />
                                )}
                            </>
                        ) : (
                            <>
                                <StockAlertsTable
                                    data={displayData}
                                    loading={loading}
                                    currency={currency}
                                    sortMode={sortMode}
                                />
                                {!showAll && allProducts.length > 5 && (
                                    <ShowMoreButton onClick={() => setShowAll(true)} />
                                )}
                            </>
                        )}
                    </TopTableContainer>
                </>
            )}
        </div>
    );
}

StockAlerts.propTypes = {
    retailer: PropTypes.string,
    currency: PropTypes.string,
    entityName: PropTypes.string,
};

export default StockAlerts;
